import React, { Component } from 'react';
import { connect, Provider } from 'react-redux';
import store from '../../redux/store/store';
import { bindActionCreators } from 'redux';
import * as calendarActions from '../../redux/actions/calendar';
import * as urlActions from '../../redux/actions/url';
import { updateSelectedDate } from '../../redux/actions/dashboard';
import { ensureFoodRange, loadFood, pollFood } from '../../redux/actions/food';
import { visibleRange } from '../components/calendar/monthGridRange';
import {
	selectBoughtListsByDay,
	selectCalendarEventsByDay,
	selectDayEvents,
	selectMealIdeasIndex,
} from '../../redux/food/selectors';


import MonthGrid from '../components/calendar/MonthGrid';
import GroceryListEdit from '../components/calendar/GroceryListEdit';
import { calendarPageViews } from 'redux/reducers/calendar';
import { slugify } from '../../utilities/slugify';
import Tabs from '../components/Tabs';
import Tab from "../components/navigation/Tab.jsx";
import MealIdeasTab from "react/components/mealIdeas/MealIdeasTab";
import MealCleanupTab from "react/components/mealIdeas/MealCleanupTab";
import CalendarSidePanel from "react/components/calendar/CalendarSidePanel";
import DayMealsModal from "react/components/calendar/DayMealsModal";
import MealSearch from "react/components/calendar/MealSearch";

class CalendarPage extends Component {

	constructor(props) {
		super(props);
		this.state = {
			loadingGroceryList: true,
			groceryList: null,
			// The day whose meals popup is open.
			popupDate: null,
			// The day cell it was opened from, which it grows out of and shrinks back into.
			popupOrigin: null,
		};
		this.dayClick = this.dayClick.bind(this);
		this.closePopup = this.closePopup.bind(this);
		this.pickSearchResult = this.pickSearchResult.bind(this);
		this.visibleRangeChange = this.visibleRangeChange.bind(this);
		this.modeChange = this.modeChange.bind(this);

		this.tabChange = this.tabChange.bind(this);
	}

	componentDidMount() {
		const { selectedDate } = this.props;
		if(selectedDate) {
			this.refresh();
		}
		this.pollWhileGroceries();
	}

	componentDidUpdate(prevProps) {
		const { selectedDate } = this.props;
		if(selectedDate && prevProps.selectedDate !== selectedDate) {
			this.fetchMonth(selectedDate);
		}
		this.pollWhileGroceries();
	}

	componentWillUnmount() {
		if(this.stopPolling) {
			this.stopPolling();
			this.stopPolling = null;
		}
	}

	/**
	 * The Groceries tab polls the food store while it is the tab shown, so other people's ticks show
	 * (pollFood). Its polls fetch no months, so leaving it fetches again those the snapshots made stale.
	 */
	pollWhileGroceries() {
		// The url slice keeps the tab's slug, as Tabs names it.
		const shown = this.props.currentCalendarViewMode === slugify(calendarPageViews.GROCERIES);
		if(shown && !this.stopPolling) {
			this.stopPolling = this.props.pollFood();
		} else if(!shown && this.stopPolling) {
			this.stopPolling();
			this.stopPolling = null;
			const { start, end } = visibleRange(this.props.selectedDate);
			this.props.ensureFoodRange(start, end.clone().subtract(1, 'day'));
		}
	}

	/**
	 * A month outside what the food store holds is fetched; one inside it is already on the grid. With
	 * no copy of the budget and a failed snapshot nothing is: the calendar stays empty until a snapshot
	 * arrives (the legacy budget/meals is no longer called, docs/local-first-food-data.md, phase 5).
	 */
	fetchMonth(selectedDate) {
		const { start, end } = visibleRange(selectedDate);
		this.props.ensureFoodRange(start, end.clone().subtract(1, 'day'));
	}

	visibleRangeChange(start, end) {
		this.props.populateVisibleDates(start, end);
	}

	modeChange(e) {
		const { changeCalendarViewMode } = this.props;
		changeCalendarViewMode(e.target.value);
	}

	/**
	 * The food store loads the lists, meals, events and meal ideas (the meal chips' tooltips need
	 * them too), which the calendar reads from it; this only makes sure it has the budget, and
	 * refreshes a copy gone stale.
	 */
	refresh() {
		const { selectedDate } = this.props;
		this.props.loadFood();
		this.fetchMonth(selectedDate);
	}

	dayClick(date, origin = null) {
		this.setState({ popupDate: date, popupOrigin: origin });
	}

	/** A meal found by the search: show its month, and open its day. */
	pickSearchResult(date) {
		const { selectedDate } = this.props;
		if(!selectedDate || !date.isSame(selectedDate, 'month')) {
			this.props.updateSelectedDate(date.clone().startOf('month'));
		}
		this.dayClick(date);
	}

	closePopup() {
		this.setState({ popupDate: null, popupOrigin: null });
	}

	renderCalendar() {
		const {
			dayEvents,
			eventsByDay,
			boughtListsByDay,
			selectedDate,
			calendarDragging,
			mealIdeasById,
		} = this.props;

		if(!selectedDate) {
			return (
				<div className='container'>
					<h5>Calendar only works for single month views</h5>
				</div>
			);
		}
		return (
			<React.Fragment>
				<MonthGrid
					selectedDate={selectedDate}
					dayEvents={dayEvents}
					eventsByDay={eventsByDay}
					boughtListsByDay={boughtListsByDay}
					dragging={calendarDragging}
					mealIdeasById={mealIdeasById}
					onVisibleRange={this.visibleRangeChange}
					onDayClick={this.dayClick}
					actions={<MealSearch mealIdeasById={mealIdeasById} onPick={this.pickSearchResult} />}
				/>
				{this.state.popupDate &&
				<Provider store={store}>
					<DayMealsModal
						key={this.state.popupDate.format('LL')}
						date={this.state.popupDate}
						origin={this.state.popupOrigin}
						onClose={this.closePopup}
					/>
				</Provider>
				}
			</React.Fragment>
		);
	}

	renderGroceryLists() {
		return (
			<div className='grocery_list'>
				<Provider store={store}>
					<GroceryListEdit />
				</Provider>
			</div>
		);
	}

	tabChange(tab) {
		const { changeCalendarViewMode } = this.props;
		changeCalendarViewMode(tab);
	}

	render() {
		const {
			selectedDate,
			currentCalendarViewMode
		} = this.props;

		return (
			<div className='container-full'>
				<Tabs
					activeTab={currentCalendarViewMode}
					onTabChange={this.tabChange}
					tabsClasses='container'
					tabContentClasses='calendar-tab-content'
					swipeable
					swipeDisabled={!!this.props.calendarDragging}
				>
					<Tab content='Grocery Calendar' label={calendarPageViews.GROCERY_CALENDAR} icon='today'>
						<div className='row calendar-row'>
							<div className='col s12 m12 l8 no-padding calendar-body'>
								{this.renderCalendar()}
							</div>
							<div className='col l4'>
								<CalendarSidePanel
									mealIdeas={
										<Provider store={store}>
											<MealIdeasTab />
										</Provider>
									}
									groceryList={this.renderGroceryLists()}
								/>
							</div>
						</div>
					</Tab>
					<Tab content='Meal Ideas' label={calendarPageViews.MEAL_IDEAS} icon='restaurant_menu'>
						<div className='container'>
							<Provider store={store}>
								<MealIdeasTab />
							</Provider>
						</div>
					</Tab>
					<Tab
						content='Groceries'
						label={calendarPageViews.GROCERIES}
						icon='shopping_cart'>
						<div className='container'>
							{this.renderGroceryLists()}
						</div>
					</Tab>
					<Tab content='Cleanup' label={calendarPageViews.MEAL_CLEANUP} icon='playlist_add_check'>
						<div className='container'>
							<Provider store={store}>
								<MealCleanupTab />
							</Provider>
						</div>
					</Tab>
				</Tabs>
			</div>
		);
	}
}

/** The first and last day ('YYYY-MM-DD') the grid shows for `selectedDate`, kept for the last one asked. */
let shownDays = { selectedDate: null, from: null, to: null };
const daysShownFor = (selectedDate) => {
	if(shownDays.selectedDate !== selectedDate) {
		const { start, end } = visibleRange(selectedDate);
		shownDays = { selectedDate, from: start.format('YYYY-MM-DD'), to: end.clone().subtract(1, 'day').format('YYYY-MM-DD') };
	}
	return shownDays;
};

export function mapStateToProps(state) {
	const { selectedDate } = state.dashboard;
	const shown = selectedDate ? daysShownFor(selectedDate) : null;
	return {
		...state.calendar,
		...state.dashboard,
		...state.url,
		// Every day on the grid, those of the months either side included.
		dayEvents: shown ? selectDayEvents(shown.from, shown.to)(state.food.entities) : null,
		eventsByDay: shown ? selectCalendarEventsByDay(shown.from, shown.to)(state.food.entities) : null,
		boughtListsByDay: shown ? selectBoughtListsByDay(shown.from, shown.to)(state.food.entities) : null,
		mealIdeasById: selectMealIdeasIndex(state).ideasById,
	};
}

function mapDispatchToProps(dispatch) {
	return {
		...bindActionCreators({
			...calendarActions,
			...urlActions,
			ensureFoodRange,
			loadFood,
			updateSelectedDate,
		}, dispatch),
		// Not an action: it starts polling and hands back the function that stops it.
		pollFood: () => pollFood(dispatch),
	};
}

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(CalendarPage);